/**
 * ErrorBoundary global de la app.
 *
 * Captura cualquier ReferenceError / TypeError sin propagar y muestra
 * una pantalla amigable con opciones de recuperación. Evita que un solo
 * import olvidado tumbe la app entera y deje al usuario en "Cargando..."
 * infinito.
 */
import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log a consola para diagnóstico (testing/dev). En producción Sentry/PostHog podría engancharse aquí.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    // Reporte al backend (best-effort, sin bloquear render)
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      fetch(`${base}/api/diag/frontend-error`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: String(error?.message || error || "Unknown error"),
          stack: error?.stack || "",
          component_stack: info?.componentStack || "",
          url: typeof window !== "undefined" ? window.location.href : "",
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      }).catch(() => {});
    } catch {
      // swallow — no queremos romper el fallback si el reporte falla
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || "Error desconocido";

    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen bg-[#0a0a0c] text-zinc-200 flex items-center justify-center p-6"
      >
        <div className="max-w-lg w-full bg-zinc-900/60 border border-amber-500/20 rounded-2xl p-8 backdrop-blur-md">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-zinc-100">Algo ha fallado</h1>
              <p className="text-sm text-zinc-400">No te preocupes, tus datos están a salvo.</p>
            </div>
          </div>

          <div className="bg-zinc-950/60 border border-zinc-800 rounded-lg p-3 mb-5 text-xs text-zinc-400 font-mono break-words">
            {msg}
          </div>

          <p className="text-sm text-zinc-400 mb-5 leading-relaxed">
            Esta pantalla concreta tuvo un error. Puedes recargar la página o volver al inicio.
            Si vuelve a ocurrir, avisa al equipo técnico con el mensaje de arriba.
          </p>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              data-testid="error-boundary-reload-btn"
              onClick={this.handleReload}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold py-2.5 px-4 rounded-lg transition"
            >
              <RefreshCw className="w-4 h-4" /> Recargar
            </button>
            <button
              data-testid="error-boundary-home-btn"
              onClick={this.handleHome}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-medium py-2.5 px-4 rounded-lg transition"
            >
              <Home className="w-4 h-4" /> Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
